import { useState, useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useTenant } from '../../contexts/TenantContext';
import { deliveriesService } from '../../services/deliveries.service';

const DEL_STATUS_COLOR = { Pending:'#f5a623',Dispatched:'#5b9cf6','In Transit':'#b89de8',Delivered:'#00e5a0',Failed:'#e05757' };
const DEL_STATUS_ICON  = { Pending:'⏳',Dispatched:'📤','In Transit':'🚚',Delivered:'✅',Failed:'✕' };
const PRIORITY_COLOR   = { Low:'#607080',Normal:'#5b9cf6',High:'#f5a623',Urgent:'#e05757' };
const PIPELINE         = ['Pending','Dispatched','In Transit','Delivered'];
const DEFAULT_CARRIERS = ['Pos Laju','DHL Express','J&T Express','GDex','City-Link'];

function Lbl({ c }) { return <div style={{ fontSize:10,color:'#3a5060',textTransform:'uppercase',letterSpacing:'1px',fontWeight:600,marginBottom:6 }}>{c}</div>; }
function Modal({ title, sub, onClose, children, wide }) {
  return <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.88)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{ background:'#090e15',border:'1px solid #1a2a3a',borderRadius:14,width:'100%',maxWidth:wide?780:640,maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column' }}>
      <div style={{ padding:'20px 24px',borderBottom:'1px solid #1a2a3a',flexShrink:0 }}>
        <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2' }}>{title}</div>
        {sub&&<div style={{ fontSize:11,color:'#3a5060',marginTop:3,fontFamily:"'IBM Plex Mono',monospace" }}>{sub}</div>}
      </div>
      <div style={{ padding:24,overflowY:'auto',flex:1 }}>{children}</div>
    </div>
  </div>;
}

export default function DeliveriesPage() {
  const { deliveries, setDeliveries, requests, setRequests, assets, facilities, profile, perms, settings, showToast } = useApp();
  const { incrementUsage, reload: reloadTenant } = useTenant();
  const [tab,setTab]             = useState('all');
  const [modal,setModal]         = useState(null);
  const [viewDel,setViewDel]     = useState(null);
  const [form,setForm]           = useState({});
  const [search,setSearch]       = useState('');
  const [saving,setSaving]       = useState(false);
  const [assetSearch,setAssetSearch] = useState('');
  const [showAssetPicker,setShowAssetPicker] = useState(false);

  const CARRIERS = (settings?.carriers||[]).filter(c=>c.active).map(c=>c.name);
  const CAR_LIST = CARRIERS.length ? CARRIERS : DEFAULT_CARRIERS;

  const byStatus   = (s) => deliveries.filter(d=>d.status===s);
  const thisYear   = new Date().getFullYear();

  const monthly = useMemo(()=>{
    const counts=Array(12).fill(0);
    deliveries.filter(d=>d.status==='Delivered'&&d.deliveredAt).forEach(d=>{ const m=parseInt((d.deliveredAt||'').slice(5,7))-1; if(!isNaN(m)&&m>=0) counts[m]++; });
    return counts;
  },[deliveries]);

  const filtered = useMemo(()=>{
    let d=deliveries;
    if(tab!=='all') d=d.filter(x=>x.status===tab);
    if(search){ const q=search.toLowerCase(); d=d.filter(x=>x.assetNames?.toLowerCase().includes(q)||x.id?.toLowerCase().includes(q)||x.trackingNo?.toLowerCase().includes(q)||x.fromFacilityName?.toLowerCase().includes(q)||x.toFacilityName?.toLowerCase().includes(q)); }
    return [...d].sort((a,b)=>(b.created_at||'').localeCompare(a.created_at||''));
  },[deliveries,tab,search]);

  const unlinked = useMemo(()=>requests.filter(r=>r.status==='Completed'&&!r.deliveryId),[requests,deliveries]);

  const openNew = (prefill) => {
    const req = prefill?.id ? requests.find(r=>r.id===prefill.id) : null;
    setForm({
      from_facility_id:  prefill?.fromFacilityId || prefill?.from_facility_id || facilities[0]?.id || '',
      to_facility_id:    prefill?.toFacilityId   || prefill?.to_facility_id   || facilities[1]?.id || '',
      assetNames:        prefill?.assetName || '',
      assetIds:          prefill?.assetId ? [prefill.assetId] : [],
      linkedRequestId:   prefill?.id || '',
      carrier:           CAR_LIST[0] || 'Pos Laju',
      tracking_no:       `TRK${Math.random().toString(36).slice(2,9).toUpperCase()}`,
      priority:          prefill?.priority || 'Normal',
      estimated_delivery:'',
      notes:             '',
      items:             1,
    });
    setAssetSearch(prefill?.assetName || '');
    setShowAssetPicker(false);
    setModal('new');
  };

  const saveNew = async () => {
    if(!form.from_facility_id||!form.to_facility_id||!form.assetNames) return;
    setSaving(true);
    try {
      const fromFac = facilities.find(f=>f.id===form.from_facility_id);
      const toFac   = facilities.find(f=>f.id===form.to_facility_id);
      const newDel  = await deliveriesService.create({
        assetNames:        form.assetNames,
        assetIds:          form.assetIds || [],
        linkedRequestId:   form.linkedRequestId || null,
        fromFacilityId:    form.from_facility_id,
        fromFacilityName:  fromFac?.name || '',
        toFacilityId:      form.to_facility_id,
        toFacilityName:    toFac?.name || '',
        carrier:           form.carrier,
        trackingNo:        form.tracking_no,
        priority:          form.priority,
        estimatedDelivery: form.estimated_delivery || null,
        notes:             form.notes,
        items:             form.items || 1,
        status:            'Pending',
        createdBy:         profile?.name,
        createdAt:         new Date().toISOString(),
        org_id:            profile?.org_id,
      });
      setDeliveries(prev=>[newDel,...prev]);
      if(form.linkedRequestId) setRequests(prev=>prev.map(r=>r.id===form.linkedRequestId?{...r,deliveryId:newDel.id}:r));
      showToast('✓ Delivery created');
      try { await incrementUsage('deliveries'); } catch(e){} reloadTenant();
      setModal(null);
      setAssetSearch('');
    } catch(err){ showToast('❌ '+err.message,'error'); }
    setSaving(false);
  };

  const updateStatus = async (delId, newStatus) => {
    try {
      const update = { status:newStatus };
      if(newStatus==='Dispatched') update.dispatched_at=new Date().toISOString();
      if(newStatus==='Delivered')  update.delivered_at=new Date().toISOString();
      await deliveriesService.update(delId, update);
      setDeliveries(prev=>prev.map(d=>d.id===delId?{...d,...update}:d));
      setViewDel(prev=>prev?{...prev,...update}:null);
      showToast(`✓ Status → ${newStatus}`);
    } catch(err){ showToast('❌ '+err.message,'error'); }
  };

  const nextStatus = (cur) => { const i=PIPELINE.indexOf(cur); return i>=0&&i<PIPELINE.length-1?PIPELINE[i+1]:null; };
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const TABS = [{id:'all',label:'All',count:deliveries.length},...['Pending','Dispatched','In Transit','Delivered','Failed'].map(s=>({id:s,label:s,count:byStatus(s).length}))];

  return <div>
    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24 }}>
      <div>
        <div style={{ fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800,color:'#e8edf2' }}>Delivery Management</div>
        <div style={{ fontSize:12,color:'#3a5060',marginTop:4 }}>{deliveries.length} total · {byStatus('In Transit').length} in transit · {byStatus('Pending').length} pending</div>
      </div>
      {perms.canCheckout&&<button className="btn" style={{ background:'#00e5a0',color:'#060b10' }} onClick={()=>openNew(null)}>+ New Delivery</button>}
    </div>

    <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:20 }}>
      {[{label:'Total',value:deliveries.length,c:'#00e5a0',icon:'📦'},{label:'Avg Monthly',value:(monthly.reduce((a,b)=>a+b,0)/12).toFixed(1),c:'#5b9cf6',icon:'📈'},{label:'Pending',value:byStatus('Pending').length,c:'#f5a623',icon:'⏳'},{label:'Delivered',value:byStatus('Delivered').length,c:'#00e5a0',icon:'✅'}].map(s=>(
        <div key={s.label} style={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:12,padding:20 }}>
          <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:8 }}>
            <div style={{ width:32,height:32,borderRadius:8,background:s.c+'18',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16 }}>{s.icon}</div>
            <div style={{ fontSize:10,color:'#3a5060',fontWeight:600,textTransform:'uppercase' }}>{s.label}</div>
          </div>
          <div style={{ fontSize:26,fontWeight:800,color:'#e8edf2',fontFamily:"'Syne',sans-serif" }}>{s.value}</div>
        </div>
      ))}
    </div>

    {/* Monthly chart */}
    <div style={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:12,padding:20,marginBottom:16 }}>
      <div style={{ fontSize:11,color:'#3a5060',textTransform:'uppercase',letterSpacing:'1.2px',fontWeight:600,marginBottom:16 }}>Monthly Deliveries — {thisYear}</div>
      <div style={{ display:'flex',alignItems:'flex-end',gap:6,height:80 }}>
        {monthly.map((count,i)=>{ const max=Math.max(...monthly,1); const h=Math.max((count/max)*68,count>0?6:1); return (
          <div key={i} style={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:5 }}>
            <div style={{ fontSize:10,color:count>0?'#00e5a0':'#3a5060',fontFamily:"'IBM Plex Mono',monospace",fontWeight:600 }}>{count||''}</div>
            <div style={{ width:'100%',height:h,background:count>0?'linear-gradient(180deg,#00e5a0,#0a9e6e)':'#1a2a3a',borderRadius:'3px 3px 0 0' }}/>
            <div style={{ fontSize:9,color:'#3a5060',fontFamily:"'IBM Plex Mono',monospace" }}>{MONTHS[i]}</div>
          </div>
        );})}
      </div>
    </div>

    {unlinked.length>0&&perms.canCheckout&&<div style={{ background:'#f5a62310',border:'1px solid #f5a62340',borderRadius:10,padding:'14px 18px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10 }}>
      <div>
        <div style={{ fontSize:13,fontWeight:600,color:'#f5a623' }}>⚡ {unlinked.length} completed request{unlinked.length>1?'s':''} without a delivery</div>
        <div style={{ fontSize:11,color:'#3a5060',marginTop:2 }}>Create deliveries from completed transfer requests</div>
      </div>
      <div style={{ display:'flex',gap:8 }}>
        {unlinked.slice(0,2).map(r=><button key={r.id} className="btn" style={{ background:'#f5a62320',border:'1px solid #f5a62340',color:'#f5a623',fontSize:11 }} onClick={()=>openNew(r)}>+ Delivery for {r.id?.slice(0,8)}</button>)}
      </div>
    </div>}

    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:10 }}>
      <div style={{ display:'flex',gap:2,flexWrap:'wrap' }}>
        {TABS.map(t=><button key={t.id} className="tab-btn" onClick={()=>setTab(t.id)} style={{ color:tab===t.id?(DEL_STATUS_COLOR[t.id]||'#00e5a0'):'#3a5060',borderBottom:tab===t.id?`2px solid ${DEL_STATUS_COLOR[t.id]||'#00e5a0'}`:'2px solid transparent',padding:'10px 14px' }}>
          {t.label}<span style={{ marginLeft:5,background:'#1a2a3a',borderRadius:8,padding:'1px 6px',fontSize:10,color:'#607080' }}>{t.count}</span>
        </button>)}
      </div>
      <div style={{ position:'relative' }}>
        <span style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'#3a5060' }}>🔍</span>
        <input className="inp" style={{ paddingLeft:30,width:240 }} placeholder="Search ID, tracking, facility…" value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
    </div>

    <div style={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:12,overflow:'hidden' }}>
      <table style={{ width:'100%',borderCollapse:'collapse' }}>
        <thead><tr style={{ borderBottom:'1px solid #1a2a3a' }}>
          {['Delivery ID','Linked REQ','Contents','From','To','Carrier','Tracking No','Priority','Status','Created'].map(h=><th key={h} className="th">{h}</th>)}
        </tr></thead>
        <tbody>
          {filtered.length===0&&<tr><td colSpan={10} style={{ textAlign:'center',padding:'40px 0',color:'#3a5060' }}>No deliveries found</td></tr>}
          {filtered.map((d,i)=><tr key={d.id} className="row" onClick={()=>setViewDel(d)} style={{ borderBottom:i<filtered.length-1?'1px solid #0d1520':'none' }}>
            <td className="td"><span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:'#5b9cf6' }}>{d.id?.slice(0,8)}</span></td>
            <td className="td"><span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:'#b89de8' }}>{d.linkedRequestId?.slice(0,8)||'—'}</span></td>
            <td className="td"><span style={{ fontWeight:600,color:'#e8edf2',maxWidth:140,display:'block',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{d.assetNames}</span></td>
            <td className="td"><span style={{ fontSize:12,color:'#607080' }}>🏭 {d.fromFacilityName}</span></td>
            <td className="td"><span style={{ fontSize:12,color:'#c8d6e0' }}>📍 {d.toFacilityName}</span></td>
            <td className="td"><span style={{ fontSize:12,color:'#c8d6e0' }}>{d.carrier}</span></td>
            <td className="td"><span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:'#f5a623' }}>{d.trackingNo}</span></td>
            <td className="td"><span style={{ color:PRIORITY_COLOR[d.priority]||'#607080',fontWeight:600,fontSize:11 }}>{d.priority}</span></td>
            <td className="td"><span style={{ display:'inline-flex',alignItems:'center',gap:5,padding:'3px 9px',borderRadius:20,fontSize:11,fontWeight:600,background:(DEL_STATUS_COLOR[d.status]||'#607080')+'18',color:DEL_STATUS_COLOR[d.status]||'#607080',border:`1px solid ${(DEL_STATUS_COLOR[d.status]||'#607080')}38` }}>{DEL_STATUS_ICON[d.status]} {d.status}</span></td>
            <td className="td"><span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:'#3a5060' }}>{d.createdAt?.slice(0,10)}</span></td>
          </tr>)}
        </tbody>
      </table>
    </div>

    {/* New Delivery Modal */}
    {modal==='new'&&<Modal title="Create New Delivery" onClose={()=>setModal(null)} wide>
      {form.linkedRequestId&&<div style={{ background:'#b89de810',border:'1px solid #b89de830',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#b89de8' }}>⇄ Synced from Transfer Request <span style={{ fontFamily:"'IBM Plex Mono',monospace",fontWeight:700 }}>{form.linkedRequestId?.slice(0,8)}</span></div>}
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14 }}>
        {/* Asset picker */}
        <div style={{ gridColumn:'1/-1' }}>
          <Lbl c="Asset / Contents *"/>
          <div style={{ position:'relative' }}>
            <input className="inp" value={assetSearch||form.assetNames||''} placeholder="Search asset by name or inventory no…"
              onChange={e=>{ setAssetSearch(e.target.value); setShowAssetPicker(true); setForm(f=>({...f,assetNames:e.target.value,assetIds:[]})); }}
              onFocus={()=>setShowAssetPicker(true)}/>
            {showAssetPicker&&assetSearch&&(
              <div style={{ position:'absolute',top:'100%',left:0,right:0,background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:8,zIndex:100,maxHeight:180,overflowY:'auto',marginTop:4 }}>
                {assets.filter(a=>a.status==='Available'&&(a.name?.toLowerCase().includes(assetSearch.toLowerCase())||a.inventoryNumber?.toLowerCase().includes(assetSearch.toLowerCase()))).slice(0,8).map(a=>(
                  <div key={a.id} style={{ padding:'10px 14px',cursor:'pointer',borderBottom:'1px solid #1a2a3a' }}
                    onMouseDown={()=>{ setForm(f=>({...f,assetNames:a.name,assetIds:[a.id],from_facility_id:a.currentFacility||f.from_facility_id})); setAssetSearch(a.name); setShowAssetPicker(false); }}>
                    <div style={{ fontSize:13,fontWeight:600,color:'#e8edf2' }}>{a.name}</div>
                    <div style={{ fontSize:11,color:'#3a5060',fontFamily:"'IBM Plex Mono',monospace" }}>{a.inventoryNumber} · {a.currentFacilityName}</div>
                  </div>
                ))}
                {assets.filter(a=>a.status==='Available'&&(a.name?.toLowerCase().includes(assetSearch.toLowerCase())||a.inventoryNumber?.toLowerCase().includes(assetSearch.toLowerCase()))).length===0&&(
                  <div style={{ padding:'12px 14px',color:'#3a5060',fontSize:12 }}>No available assets found</div>
                )}
              </div>
            )}
          </div>
          {form.assetIds?.length>0&&<div style={{ fontSize:11,color:'#00e5a0',marginTop:4 }}>✓ Asset selected · auto-filled From Facility</div>}
        </div>
        {/* Link to request */}
        <div style={{ gridColumn:'1/-1' }}>
          <Lbl c="Link to Transfer Request (optional)"/>
          <select className="sel" style={{ width:'100%' }} value={form.linkedRequestId||''} onChange={e=>{
            const req=requests.find(r=>r.id===e.target.value);
            if(req) setForm(f=>({...f,linkedRequestId:req.id,assetNames:req.assetName||f.assetNames,from_facility_id:req.fromFacilityId||f.from_facility_id,to_facility_id:req.toFacilityId||f.to_facility_id}));
            else setForm(f=>({...f,linkedRequestId:''}));
          }}>
            <option value="">— None —</option>
            {requests.filter(r=>r.status==='Approved'||r.status==='Completed').map(r=>(
              <option key={r.id} value={r.id}>{r.id.slice(0,8)} · {r.assetName} · {r.fromFacilityName}→{r.toFacilityName}</option>
            ))}
          </select>
        </div>
        <div><Lbl c="From Facility *"/><select className="sel" style={{ width:'100%' }} value={form.from_facility_id||''} onChange={e=>setForm(f=>({...f,from_facility_id:e.target.value}))}>{facilities.map(f=><option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
        <div><Lbl c="To Facility *"/><select className="sel" style={{ width:'100%' }} value={form.to_facility_id||''} onChange={e=>setForm(f=>({...f,to_facility_id:e.target.value}))}>{facilities.map(f=><option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
        <div><Lbl c="Carrier"/><select className="sel" style={{ width:'100%' }} value={form.carrier||'Pos Laju'} onChange={e=>setForm(f=>({...f,carrier:e.target.value}))}>{CAR_LIST.map(c=><option key={c}>{c}</option>)}</select></div>
        <div><Lbl c="Tracking Number"/><input className="inp" style={{ fontFamily:"'IBM Plex Mono',monospace" }} value={form.tracking_no||''} onChange={e=>setForm(f=>({...f,tracking_no:e.target.value}))}/></div>
        <div><Lbl c="Priority"/><select className="sel" style={{ width:'100%' }} value={form.priority||'Normal'} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>{['Low','Normal','High','Urgent'].map(p=><option key={p}>{p}</option>)}</select></div>
        <div><Lbl c="Estimated Delivery"/><input className="inp" type="date" value={form.estimated_delivery||''} onChange={e=>setForm(f=>({...f,estimated_delivery:e.target.value}))}/></div>
        <div><Lbl c="No. of Items"/><input className="inp" type="number" min={1} value={form.items||1} onChange={e=>setForm(f=>({...f,items:parseInt(e.target.value)||1}))}/></div>
        <div style={{ gridColumn:'1/-1' }}><Lbl c="Notes"/><textarea className="inp" style={{ resize:'vertical',minHeight:60 }} value={form.notes||''} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div>
      </div>
      <div style={{ display:'flex',gap:10,marginTop:18 }}>
        <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#607080' }} onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn" style={{ flex:2,background:'#00e5a0',color:'#060b10' }} onClick={saveNew} disabled={saving}>{saving?'Saving…':'Create Delivery'}</button>
      </div>
    </Modal>}

    {/* View/Manage Modal */}
    {viewDel&&<Modal title={`Delivery ${viewDel.id?.slice(0,8)}`} sub={`${viewDel.fromFacilityName} → ${viewDel.toFacilityName}`} onClose={()=>setViewDel(null)} wide>
      {/* Pipeline */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:10,color:'#3a5060',textTransform:'uppercase',letterSpacing:'1px',fontWeight:600,marginBottom:12 }}>Delivery Pipeline</div>
        <div style={{ display:'flex',alignItems:'center' }}>
          {PIPELINE.map((s,i)=>{ const done=PIPELINE.indexOf(viewDel.status)>=i; const cur=viewDel.status===s; const c=DEL_STATUS_COLOR[s]; return (
            <div key={s} style={{ display:'flex',alignItems:'center',flex:i<PIPELINE.length-1?1:'none' }}>
              <div style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:5 }}>
                <div style={{ width:32,height:32,borderRadius:'50%',background:done?c+'22':'#1a2a3a',border:`2px solid ${done?c:'#2a3f52'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,transition:'all .3s' }}>
                  {done?<span style={{ color:c }}>{DEL_STATUS_ICON[s]}</span>:<span style={{ color:'#3a5060' }}>○</span>}
                </div>
                <div style={{ fontSize:10,color:cur?c:'#3a5060',fontWeight:cur?700:400,whiteSpace:'nowrap' }}>{s}</div>
              </div>
              {i<PIPELINE.length-1&&<div style={{ flex:1,height:2,background:PIPELINE.indexOf(viewDel.status)>i?c:'#1a2a3a',margin:'0 4px',marginBottom:18,transition:'background .3s' }}/>}
            </div>
          );})}
        </div>
      </div>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:18 }}>
        {[['Delivery ID',viewDel.id],['Linked Request',viewDel.linkedRequestId||'—'],['Contents',viewDel.assetNames],['Items',viewDel.items],['From',viewDel.fromFacilityName],['To',viewDel.toFacilityName],['Carrier',viewDel.carrier],['Tracking No.',viewDel.trackingNo],['Priority',viewDel.priority],['Created By',viewDel.createdBy],['Created At',viewDel.createdAt?.slice(0,16)],['Dispatched At',viewDel.dispatchedAt?.slice(0,16)||'—'],['Delivered At',viewDel.deliveredAt?.slice(0,16)||'—'],['Est. Delivery',viewDel.estimatedDelivery||'—']].map(([k,v])=>(
          <div key={k} style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,padding:'10px 14px' }}>
            <div style={{ fontSize:10,color:'#3a5060',textTransform:'uppercase',letterSpacing:'1px',marginBottom:4 }}>{k}</div>
            <div style={{ fontSize:13,color:'#c8d6e0',fontFamily:['Delivery ID','Linked Request','Tracking No.'].includes(k)?"'IBM Plex Mono',monospace":'inherit' }}>{String(v||'—')}</div>
          </div>
        ))}
      </div>
      {perms.canCheckout&&viewDel.status!=='Delivered'&&viewDel.status!=='Failed'&&<div style={{ display:'flex',gap:8 }}>
        <button className="btn" style={{ flex:1,background:'#e0575720',border:'1px solid #e0575740',color:'#e05757',fontSize:12 }} onClick={()=>updateStatus(viewDel.id,'Failed')}>✕ Mark Failed</button>
        {nextStatus(viewDel.status)&&<button className="btn" style={{ flex:2,background:(DEL_STATUS_COLOR[nextStatus(viewDel.status)]||'#00e5a0')+'20',border:`1px solid ${(DEL_STATUS_COLOR[nextStatus(viewDel.status)]||'#00e5a0')}40`,color:DEL_STATUS_COLOR[nextStatus(viewDel.status)]||'#00e5a0',fontSize:12 }} onClick={()=>updateStatus(viewDel.id,nextStatus(viewDel.status))}>
          {DEL_STATUS_ICON[nextStatus(viewDel.status)]} Move to {nextStatus(viewDel.status)}
        </button>}
      </div>}
      {viewDel.status==='Delivered'&&<div style={{ textAlign:'center',padding:'12px 0',color:'#00e5a0',fontSize:13,fontWeight:600 }}>✅ Delivered on {viewDel.deliveredAt?.slice(0,10)}</div>}
      {viewDel.status==='Failed'   &&<div style={{ textAlign:'center',padding:'12px 0',color:'#e05757',fontSize:13,fontWeight:600 }}>✕ Marked as failed</div>}
    </Modal>}
  </div>;
}
